import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { Listbox } from "@headlessui/react";
import { FiChevronDown, FiCheck, FiSearch } from "react-icons/fi";
import Button from "@/components/loading-button";
import useStore from "@/services/store";
import api from "@/services/api";

// Select component (inlined)
function Select({ placeholder = "select", data, value, onChange, className = "w-[200px]" }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);

  const isObjectData = Array.isArray(data) && data.length > 0 && typeof data[0] === "object";
  const selectedLabel = isObjectData ? (value ? data.find((item) => item.value === value)?.label : null) || placeholder : value || placeholder;

  useEffect(() => {
    setFilteredOptions(data || []);
  }, [data]);

  useEffect(() => {
    if (searchTerm?.trim() === "") {
      setFilteredOptions(data || []);
    } else {
      const filtered = (data || []).filter((option) => {
        const label = isObjectData ? option.label : option;
        return label?.toString().toLowerCase().includes(searchTerm?.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, data, isObjectData]);

  const handleSearch = (e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative z-20">
        <Listbox.Button
          className={`relative capitalize cursor-pointer bg-white py-2 pl-3 pr-2 text-left border-0 rounded ring-1 ring-gray-300 sm:text-sm flex items-center justify-between gap-2 ${className}`}
        >
          <span className="whitespace-nowrap truncate">{selectedLabel}</span>
          <FiChevronDown className="text-xl" aria-hidden="true" />
        </Listbox.Button>
        <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto bg-white border border-gray-200 rounded-md py-1 text-base shadow-lg focus:outline-none sm:text-sm">
          <div className="sticky top-0 bg-white p-1 border-b border-gray-200 flex items-center z-50">
            <div className="relative flex-1 bg-white">
              <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <div className="absolute inset-0 bg-white z-50 -top-10 left-0 w-full h-full"></div>
              <input
                type="text"
                className="w-full !pl-8 pr-2 py-1 bg-white text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={placeholder}
                value={searchTerm}
                onChange={handleSearch}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="py-2 px-3 text-gray-500 text-sm">No results found</div>
          ) : (
            filteredOptions.map((option) => {
              const optionValue = isObjectData ? option.value : option;
              const optionLabel = isObjectData ? option.label : option;

              return (
                <Listbox.Option
                  key={optionValue}
                  value={optionValue}
                  className={({ active }) => `cursor-pointer select-none relative py-2 pl-10 pr-4 ${active ? "text-black bg-primary/10" : "text-gray-900"}`}
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate capitalize ${selected ? "font-medium" : "font-normal"}`}>{optionLabel}</span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                          <FiCheck className="w-5 h-5 text-indigo-600" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              );
            })
          )}
        </Listbox.Options>
      </div>
    </Listbox>
  );
}

const translations = {
  en: {},
  fr: {},
};

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
];

export default function ProfileSettings() {
  const { i18n } = useComponentTranslation(translations);
  const { user, setUser } = useStore();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});

  useEffect(() => {
    if (user) setValues(user);
  }, [user]);

  const handleLanguageChange = (newLanguage) => {
    setValues({ ...values, language: newLanguage });
    // Immediately change the UI language for better UX
    i18n.changeLanguage(newLanguage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Split name into firstname and lastname if needed
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        timezone: values.timezone,
        language: values.language,
      };

      const { ok, data } = await api.put("/user", payload);
      if (!ok) throw new Error("Error updating profile");
      setUser(data);
      // Ensure i18n language matches saved preference
      if (data.language) {
        i18n.changeLanguage(data.language.toLowerCase());
      }
      toast.success("Profile updated successfully");
    } catch (e) {
      console.log(e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Profile Settings</h1>

      <form onSubmit={handleSubmit} className="w-full">
        <div className="grid grid-cols-2 gap-6">
          {/* Name Section */}
          <div>
            <label htmlFor="name" className="input-label">
              First name
            </label>
            <input
              id="firstname"
              type="text"
              value={values.firstname}
              onChange={(e) => setValues({ ...values, firstname: e.target.value })}
              className="input"
              placeholder="Enter your first name"
              required
            />
          </div>
          <div>
            <label htmlFor="lastname" className="input-label">
              Last name
            </label>
            <input
              id="lastname"
              type="text"
              value={values.lastname}
              onChange={(e) => setValues({ ...values, lastname: e.target.value })}
              className="input"
              placeholder="Enter your last name"
              required
            />
          </div>

          {/* Email Section */}
          <div>
            <label htmlFor="email" className="input-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={values.email}
              onChange={(e) => setValues({ ...values, email: e.target.value })}
              className="input"
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Language Section */}
          <div>
            <label htmlFor="language" className="input-label">
              Language
            </label>
            <Select placeholder="Select language" data={LANGUAGES} value={values.language} onChange={handleLanguageChange} className="w-full" />
          </div>
        </div>

        {/* Save Changes Button */}
        <div className="flex justify-end mt-4">
          <Button loading={loading} type="submit" className="btn btn-primary">
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
}
